import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Button,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import DocumentNavigation from './DocumentNavigation';
import API_BASE_URL from '../api';
import bill from '../assets/bill.jpg';
import logo from '../assets/log.png';
import stamp from '../assets/stamp.jpg';
import { printWhenReady } from './printUtils';

const companyDetails = [
  ['Company Name', 'LAR Contracting Company'],
  ['Short Address', 'ENAA8599'],
  ['Secondary No', '2287'],
  ['Customer Account', '31335115107'],
  ['Building No', '8599'],
  ['City', 'Prince Saud Ibn Abdullah Ibn Jalawi Branch, Al Jubail'],
  ['District', 'Tebah Dist.'],
  ['Country', 'Kingdom of Saudi Arabia'],
  ['Postal Code', '35513'],
  ['VAT Number', '314702232800003'],
  ['CRN', '7053932963'],
  ['Bank', 'Al Rajhi Bank'],
  ['Account Name', 'LAR Contracting Company'],
  ['Account Number', '51300-001-0006085423395'],
  ['IBAN', 'SA5380000513608015423395']
];

const theme = createTheme({
  palette: {
    primary: { main: '#0B1F3A', dark: '#061426', light: '#173D68', contrastText: '#FFFFFF' },
    secondary: { main: '#E85D3F', dark: '#C9432B', light: '#FF9A78', contrastText: '#0B1F3A' },
    background: { default: '#F4F7FB', paper: '#FFFFFF' },
    text: { primary: '#172033', secondary: '#667085' }
  },
  typography: {
    fontFamily: 'Inter, "Segoe UI", Arial, sans-serif',
    h4: { fontWeight: 900 },
    h6: { fontWeight: 800 },
    button: { textTransform: 'none', fontWeight: 800 }
  },
  shape: { borderRadius: 14 }
});

const sectionTitleSx = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  mt: 3,
  mb: 1.5,
  px: 1.5,
  py: 1.1,
  borderRadius: '12px 12px 12px 4px',
  color: '#FFFFFF',
  background: 'linear-gradient(135deg, #0B1F3A 0%, #173D68 100%)',
  boxShadow: '0 8px 22px rgba(11,31,58,.12)',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    right: 0,
    top: 0,
    width: 6,
    height: '100%',
    background: '#E85D3F'
  }
};

const emptyClient = {
  name: '',
  address: '',
  district: '',
  postalCode: '',
  country: '',
  additionalNo: '',
  vatNumber: '',
  crn: ''
};

const createProduct = () => ({
  description: '',
  unitRate: '',
  quantity: '',
  unit: 'UNIT',
  vatRate: '15'
});

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;'
}[character]));

const formatAmount = (amount) => amount.toFixed(2);

export default function Quote() {
  const [quoteNumber, setQuoteNumber] = useState(null);
  const [loadingNumber, setLoadingNumber] = useState(true);
  const [client, setClient] = useState(emptyClient);
  const [quoteDetails, setQuoteDetails] = useState({
    requestedDate: '',
    workStartDate: '',
    quoteDate: new Date().toISOString().slice(0, 10),
    validUntil: '',
    contractNo: '',
    projectRefNo: '',
    paymentTerms: ''
  });
  const [products, setProducts] = useState([createProduct()]);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    const fetchNextQuoteNumber = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/quote/next-quote-number`);
        setQuoteNumber(response.data.nextQuoteNumber);
      } catch (error) {
        console.error('Error fetching next quote number:', error);
        setNotice(error.response?.data?.error || 'Unable to load the next quote number.');
      } finally {
        setLoadingNumber(false);
      }
    };
    fetchNextQuoteNumber();
  }, []);

  const lineAmounts = products.map((product) => {
    const subtotal = (Number(product.unitRate) || 0) * (Number(product.quantity) || 0);
    const vat = subtotal * (Number(product.vatRate) || 0) / 100;
    return { subtotal, vat, total: subtotal + vat };
  });
  const totals = lineAmounts.reduce((result, line) => ({
    subtotal: result.subtotal + line.subtotal,
    vat: result.vat + line.vat,
    total: result.total + line.total
  }), { subtotal: 0, vat: 0, total: 0 });

  const updateQuoteDetail = (event) => {
    const { name, value } = event.target;
    setQuoteDetails((current) => ({ ...current, [name]: value }));
  };

  const updateProduct = (index, event) => {
    const { name, value } = event.target;
    setProducts((current) => current.map((product, productIndex) => (
      productIndex === index ? { ...product, [name]: value } : product
    )));
  };

  const saveQuote = async () => {
    if (!Number.isInteger(quoteNumber)) {
      setNotice('Quote number is not ready. Refresh the page and try again.');
      return;
    }
    const quote = {
      quoteNumber,
      companyDetails: Object.fromEntries(companyDetails.map(([label, value]) => [label, value])),
      client,
      quoteDetails,
      products: products.map((product, index) => ({
        ...product,
        ...lineAmounts[index]
      })),
      totals
    };

    try {
      const response = await axios.post(`${API_BASE_URL}/quote`, quote);
      if (response.status !== 201) {
        throw new Error('The quote was not saved.');
      }
      setNotice(`Quote ${quoteNumber} saved successfully.`);
    } catch (error) {
      console.error('Unable to save quote:', error);
      setNotice(error.response?.data?.error || 'Unable to save quote. Please try again.');
      return;
    }

    setQuoteNumber(null);
    setLoadingNumber(true);
    try {
      const nextNumberResponse = await axios.get(`${API_BASE_URL}/quote/next-quote-number`);
      setQuoteNumber(nextNumberResponse.data.nextQuoteNumber);
    } catch (error) {
      console.error('Quote saved, but unable to load the next quote number:', error);
      setNotice(`Quote ${quoteNumber} saved, but the next number could not be loaded. Refresh the page before saving another quote.`);
    } finally {
      setLoadingNumber(false);
    }
  };

  const printQuote = async () => {
    const printWindow = window.open('', '_blank', 'height=800,width=1000');
    if (!printWindow) {
      setNotice('The print window was blocked. Please allow pop-ups and try again.');
      return;
    }

    const transliterateToArabic = (value) => {
      const map = {
        a: 'ا', b: 'ب', c: 'ك', d: 'د', e: 'ي', f: 'ف', g: 'ج', h: 'ه',
        i: 'ي', j: 'ج', k: 'ك', l: 'ل', m: 'م', n: 'ن', o: 'و', p: 'ب',
        q: 'ق', r: 'ر', s: 'س', t: 'ت', u: 'و', v: 'ف', w: 'و', x: 'كس',
        y: 'ي', z: 'ز', ',': '،', '.': '.', '-': '-'
      };
      return Array.from(String(value ?? '')).map((character) => {
        if (/[0-9\s]/.test(character)) return character;
        return map[character.toLowerCase()] || character;
      }).join('');
    };
    const formatDate = (value) => value ? new Date(`${value}T00:00:00`).toLocaleDateString('en-GB') : '';
    const arabicCountry = (value) => (
      /saudi arabia|kingdom of saudi arabia|ksa/i.test(String(value || ''))
        ? 'المملكة العربية السعودية'
        : /india/i.test(String(value || '')) ? 'الهند' : transliterateToArabic(value)
    );
    const detailsTable = (rows) => rows.map(([englishLabel, englishValue, arabicValue, arabicLabel]) => `
      <tr>
        <th class="col2">${escapeHtml(englishLabel)}:</th>
        <td colspan="3" class="col2">${escapeHtml(englishValue || '')}</td>
        <td colspan="3" class="col2 arabic" dir="rtl">${escapeHtml(arabicValue || '')}</td>
        <th class="col2 align-end">${escapeHtml(arabicLabel)}</th>
      </tr>
    `).join('');
    const productRows = products.map((product, index) => `
      <tr>
        <td class="col1">${formatAmount(lineAmounts[index].total)}</td>
        <td class="col1">${formatAmount(lineAmounts[index].vat)}</td>
        <td class="col1">${escapeHtml(product.vatRate)}%</td>
        <td class="col1">${formatAmount(lineAmounts[index].subtotal)}</td>
        <td class="col1">${formatAmount(Number(product.unitRate) || 0)}</td>
        <td class="col1">${escapeHtml(product.quantity)}</td>
        <td class="col1">${escapeHtml(product.unit)}</td>
        <td class="colgoods">
          <div>${escapeHtml(product.description)}</div>
          <div class="arabic" dir="rtl">${escapeHtml(transliterateToArabic(product.description))}</div>
        </td>
        <td class="col1">${index + 1}</td>
      </tr>
    `).join('');

    const companyValues = Object.fromEntries(companyDetails);
    const companyRows = detailsTable([
      ['Name', companyValues['Company Name'], 'شركة إل آر كونتراكتنج', 'الاسم:'],
      ['Short Address', companyValues['Short Address'], companyValues['Short Address'], 'العنوان المختصر:'],
      ['Secondary No', companyValues['Secondary No'], companyValues['Secondary No'], 'صندوق البريد:']
    ]) + `
      <tr>
        <th class="col2">Building No:</th><td>${escapeHtml(companyValues['Building No'])}</td>
        <th class="col2">City:</th><td>${escapeHtml(companyValues.City)}</td>
        <td class="arabic" dir="rtl">فرع الأمير سعود بن عبدالله بن جلوي، الجبيل</td>
        <th class="col2 align-end">المدينة:</th>
        <td class="arabic" dir="rtl">${escapeHtml(companyValues['Building No'])}</td>
        <th class="col2 align-end">رقم المبنى:</th>
      </tr>
      <tr>
        <th class="col2">Customer Account:</th><td>${escapeHtml(companyValues['Customer Account'])}</td>
        <th class="col2">District:</th><td>${escapeHtml(companyValues.District)}</td>
        <td class="arabic" dir="rtl">حي طيبة</td><th class="col2 align-end">الحي:</th>
        <td class="arabic" dir="rtl">${escapeHtml(companyValues['Customer Account'])}</td>
        <th class="col2 align-end">رقم حساب العميل:</th>
      </tr>
      <tr>
        <th class="col2">Postal Code:</th><td>${escapeHtml(companyValues['Postal Code'])}</td>
        <th class="col2">Country:</th><td>${escapeHtml(companyValues.Country)}</td>
        <td class="arabic" dir="rtl">${arabicCountry(companyValues.Country)}</td>
        <th class="col2 align-end">الدولة:</th>
        <td class="arabic" dir="rtl">${escapeHtml(companyValues['Postal Code'])}</td>
        <th class="col2 align-end">الرمز البريدي:</th>
      </tr>
      <tr>
        <th class="col2">VAT Number:</th><td>${escapeHtml(companyValues['VAT Number'])}</td>
        <th class="col2">CRN:</th><td colspan="2">${escapeHtml(companyValues.CRN)}</td>
        <th class="col2 align-end">السجل التجاري:</th>
        <td class="arabic" dir="rtl">${escapeHtml(companyValues['VAT Number'])}</td>
        <th class="col2 align-end">رقم ضريبة القيمة المضافة:</th>
      </tr>
    `;
    const clientRows = detailsTable([
      ['Name', client.name, transliterateToArabic(client.name), 'الاسم:'],
      ['Address', client.address, transliterateToArabic(client.address), 'العنوان:']
    ]) + `
      <tr>
        <th class="col2">Addl. No:</th><td>${escapeHtml(client.additionalNo)}</td>
        <th class="col2">District:</th><td>${escapeHtml(client.district)}</td>
        <td class="arabic" dir="rtl">${escapeHtml(transliterateToArabic(client.district))}</td>
        <th class="col2 align-end">الحي:</th>
        <td class="arabic" dir="rtl">${escapeHtml(client.additionalNo)}</td>
        <th class="col2 align-end">الرقم الإضافي:</th>
      </tr>
      <tr>
        <th class="col2">Postal Code:</th><td>${escapeHtml(client.postalCode)}</td>
        <th class="col2">Country:</th><td>${escapeHtml(client.country)}</td>
        <td class="arabic" dir="rtl">${arabicCountry(client.country)}</td>
        <th class="col2 align-end">الدولة:</th>
        <td class="arabic" dir="rtl">${escapeHtml(client.postalCode)}</td>
        <th class="col2 align-end">الرمز البريدي:</th>
      </tr>
      <tr>
        <th class="col2">VAT Number:</th><td>${escapeHtml(client.vatNumber)}</td>
        <th class="col2">CRN:</th><td colspan="2">${escapeHtml(client.crn)}</td>
        <th class="col2 align-end">السجل التجاري:</th>
        <td class="arabic" dir="rtl">${escapeHtml(client.vatNumber)}</td>
        <th class="col2 align-end">رقم ضريبة القيمة المضافة:</th>
      </tr>
    `;

    printWindow.document.write(`<!doctype html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Quotation ${escapeHtml(quoteNumber)}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; padding: 0; color: #111; }
            table { width: 100%; max-width: 900px; border-collapse: collapse; margin: 14px auto 0; border: 1px solid #d8d2cc; table-layout: fixed; }
            th, td { border: 1px solid #d8d2cc; padding: 4px; text-align: left; height: 20px; line-height: 1.05; font-size: 10px; overflow-wrap: anywhere; }
            th { background-color: #f3f0eb; }
            .header-container { display: flex; justify-content: center; align-items: center; gap: 20px; }
            .header-container h1 { color: #1D2A5F; margin: 0; font-size: 20px; }
            .header-container h2 { color: #D55C36; margin: 5px 0; font-size: 14px; }
            .logo-container img { max-width: 100px; height: auto; }
            .Invoice { text-align: center; color: #1D2A5F; font-size: 18px; font-weight: 700; padding: 8px 0; margin: 0; }
            .clearfix::after { content: ""; display: table; clear: both; }
            .left-align { float: left; }
            .right-align { float: right; text-align: right; }
            .Headtitle td, .new { background-color: #1D2A5F; color: white; font-weight: bold; }
            .Headtitle h3 { margin: 0; padding: 2px; line-height: 1.2; }
            .align-end { text-align: right; direction: rtl; }
            .col2 { width: 12.5%; }
            .col1 { text-align: center; }
            .detail-date th, .detail-date td { width: 20%; text-align: center; height: 30px; }
            .detail-extra th, .detail-extra td { text-align: center; }
            .products th, .products td { text-align: center; padding: 4px 2px; }
            .products th { vertical-align: middle; height: 64px; }
            .products .colgoods { width: 30%; }
            .products .col1 { width: 8%; }
            .arabic { direction: rtl; text-align: right; font-family: Arial, sans-serif; }
            .Summary { width: 65%; margin: 12px 0 0 auto; }
            .Summary .col4 { width: 27%; }
            .Summary .col8 { width: 16%; text-align: center; }
            .Summary .col5 { width: 32%; text-align: right; direction: rtl; }
            .bank-table { margin-top: 12px; table-layout: fixed; }
            .bank-table .new { text-align: center; }
            .signature-section { text-align: center; vertical-align: bottom; height: 120px; padding-bottom: 8px; }
            .arabic-label { display: block; direction: rtl; text-align: right; }
            @media print {
              * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              body { margin: 10mm; }
              table { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header-container">
            <div style="text-align:right">
              <h1>LAR Contracting Company</h1>
              <h2>Quotation | عرض سعر</h2>
            </div>
            <div class="logo-container"><img src="${bill}" alt="LAR Contracting logo" /></div>
          </div>
          <p class="Invoice"><strong>Quotation / عرض سعر</strong></p>
          <div class="clearfix">
            <p class="left-align"><strong>Quote No:</strong> ${escapeHtml(quoteNumber)}</p>
            <p class="right-align" dir="rtl"><strong>رقم عرض السعر:</strong> ${escapeHtml(quoteNumber)}</p>
          </div>
          <table class="Detail">
            <tr class="Headtitle"><td colspan="8"><div class="clearfix"><h3 class="left-align">Our Details:</h3><h3 class="right-align" dir="rtl">تفاصيلنا:</h3></div></td></tr>
            ${companyRows}
          </table>
          <table class="Detail">
            <tr class="Headtitle"><td colspan="8"><div class="clearfix"><h3 class="left-align">Client Details:</h3><h3 class="right-align" dir="rtl">تفاصيل العميل:</h3></div></td></tr>
            ${clientRows}
          </table>
          <table class="Detail detail-date">
            <tr>
              <th>تاريخ الطلب<br>Requested Date</th>
              <th>تاريخ بدء العمل<br>Work Start Date</th>
              <th>رقم العقد / أمر الشراء<br>Contract / PO No</th>
              <th>تاريخ عرض السعر<br>Quote Date</th>
              <th>رقم المشروع / المرجع<br>Project / Reference No</th>
            </tr>
            <tr>
              <td>${escapeHtml(formatDate(quoteDetails.requestedDate))}</td>
              <td>${escapeHtml(formatDate(quoteDetails.workStartDate))}</td>
              <td>${escapeHtml(quoteDetails.contractNo)}</td>
              <td>${escapeHtml(formatDate(quoteDetails.quoteDate))}</td>
              <td>${escapeHtml(quoteDetails.projectRefNo)}</td>
            </tr>
          </table>
          <table class="Detail detail-extra">
            <tr>
              <th>صالح حتى / Valid Until</th>
              <td>${escapeHtml(formatDate(quoteDetails.validUntil))}</td>
              <th>شروط الدفع / Payment Terms</th>
              <td>${escapeHtml(quoteDetails.paymentTerms)}</td>
            </tr>
          </table>
          <table class="Detail products">
            <thead>
              <tr>
                <th>الإجمالي شامل الضريبة<br>Total Incl. VAT (SAR)</th>
                <th>مبلغ ضريبة القيمة المضافة<br>VAT Amount (SAR)</th>
                <th>نسبة الضريبة (%)<br>VAT %</th>
                <th>الإجمالي بدون ضريبة<br>Total Excl. VAT (SAR)</th>
                <th>سعر الوحدة<br>Unit Rate (SAR)</th>
                <th>الكمية<br>Qty / Hours</th>
                <th>وحدة<br>Unit</th>
                <th class="colgoods">طبيعة السلع/الخدمات<br>Nature of Goods/Services (Description)</th>
                <th>#</th>
              </tr>
            </thead>
            <tbody>${productRows}</tbody>
          </table>
          <table class="Summary">
            <tr><td class="col4">Total Excl. VAT (SAR):</td><td class="col8">${formatAmount(totals.subtotal)}</td><td class="col5">الإجمالي بدون ضريبة (ريال سعودي)</td></tr>
            <tr><td class="col4">VAT Amount (SAR):</td><td class="col8">${formatAmount(totals.vat)}</td><td class="col5">مبلغ ضريبة القيمة المضافة (ريال سعودي)</td></tr>
            <tr><td class="col4">Total Incl. VAT (SAR):</td><td class="col8">${formatAmount(totals.total)}</td><td class="col5">الإجمالي شامل الضريبة (ريال سعودي)</td></tr>
            <tr><td class="col4">Balance Due (SAR):</td><td class="col8">${formatAmount(totals.total)}</td><td class="col5">الرصيد المستحق (ر.س)</td></tr>
          </table>
          <table class="bank-table">
            <tr>
              <th colspan="4" class="new">Bank Details / التفاصيل المصرفية</th>
              <th colspan="2" class="new">Prepared By / معد الفاتورة</th>
              <th colspan="2" class="new">Received By / مستلم الفاتورة</th>
            </tr>
            <tr>
              <td colspan="2"><strong>ACCOUNT NAME</strong> / اسم الحساب</td>
              <td colspan="2">LAR Contracting Company<br><span class="arabic">شركة إل آر كونتراكتنج</span></td>
              <td colspan="2" rowspan="4" class="signature-section" style="height:140px;padding:0;"><img src="${stamp}" alt="Signature with stamp" style="width:100%;height:140px;object-fit:fill;display:block;" /></td>
              <td colspan="3" rowspan="4" class="signature-section"><span class="arabic">&nbsp;</span></td>
            </tr>
            <tr><td colspan="2"><strong>BANK</strong> / بنك</td><td colspan="2">Al Rajhi Bank</td></tr>
            <tr><td colspan="2"><strong>ACCOUNT</strong> / الحساب</td><td colspan="2">51300-001-0006085423395</td></tr>
            <tr><td colspan="2"><strong>IBAN</strong> / ايبان</td><td colspan="2">SA5380000513608015423395</td></tr>
          </table>
        </body>
      </html>`);
    printWindow.document.close();
    await printWhenReady(printWindow);
  };

  return (
    <ThemeProvider theme={theme}>
      <div
        style={{
          direction: 'rtl',
          minHeight: '100vh',
          background: 'radial-gradient(circle at 10% 0%, rgba(232,93,63,.10), transparent 28%), linear-gradient(135deg, #EEF3F8 0%, #F8FAFC 52%, #EEF2F7 100%)',
          padding: '28px 14px 50px'
        }}
      >
        <DocumentNavigation />
        <Paper
          elevation={0}
          sx={{
            position: 'relative',
            mx: 'auto',
            maxWidth: 1220,
            overflow: 'hidden',
            borderRadius: { xs: 2.5, md: 4 },
            background: 'rgba(255,255,255,.96)',
            border: '1px solid rgba(11,31,58,.08)',
            boxShadow: '0 24px 70px rgba(11,31,58,.13)',
            '&::before': {
              content: '""',
              display: 'block',
              height: 5,
              background: 'linear-gradient(90deg, #0B1F3A 0%, #173D68 55%, #E85D3F 100%)'
            }
          }}
        >
          <div
            style={{
              padding: '30px clamp(18px, 4vw, 42px)',
              background: 'linear-gradient(135deg, #08192E 0%, #0B1F3A 55%, #173D68 100%)',
              color: '#FFFFFF'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
              <div>
                <Typography sx={{ color: '#FF9A78', fontSize: 12, fontWeight: 800, letterSpacing: 1 }}>
                  LAR CONTRACTING · COMMERCIAL
                </Typography>
                <Typography variant="h4" sx={{ mt: 1, color: '#FFFFFF' }}>
                  Quotation / عرض سعر
                </Typography>
                <Typography sx={{ mt: 0.5, color: '#DDE6F0' }}>
                  Prepare a clear quotation for your client
                </Typography>
              </div>
              <img
                src={logo}
                alt="LAR Contracting Company"
                style={{ display: 'block', width: 'min(100%, 250px)', maxHeight: 90, objectFit: 'contain' }}
              />
            </div>
          </div>

          <div style={{ padding: '24px clamp(16px, 3vw, 32px) 32px' }}>
            {notice && (
              <Alert severity={notice.startsWith('Unable') || notice.startsWith('Quote number') || notice.startsWith('The print') || notice.includes('could not be loaded') ? 'error' : 'success'} sx={{ mb: 2 }}>
                {notice}
              </Alert>
            )}

            <Typography variant="h6" sx={sectionTitleSx}>Our Details</Typography>
            <Grid container spacing={2}>
              {companyDetails.map(([label, value]) => (
                <Grid item xs={12} sm={6} md={4} key={label}>
                  <TextField label={label} value={value} fullWidth InputProps={{ readOnly: true }} />
                </Grid>
              ))}
            </Grid>

            <Typography variant="h6" sx={sectionTitleSx}>Client Details</Typography>
            <Grid container spacing={2}>
              {[
                ['Client Name', 'name'],
                ['Address', 'address'],
                ['District', 'district'],
                ['Postal Code', 'postalCode'],
                ['Country', 'country'],
                ['Additional No', 'additionalNo'],
                ['VAT Number', 'vatNumber'],
                ['CRN', 'crn']
              ].map(([label, name]) => (
                <Grid item xs={12} sm={6} md={4} key={name}>
                  <TextField
                    label={label}
                    name={name}
                    value={client[name]}
                    onChange={(event) => setClient((current) => ({
                      ...current,
                      [event.target.name]: event.target.value
                    }))}
                    fullWidth
                  />
                </Grid>
              ))}
            </Grid>

            <Typography variant="h6" sx={sectionTitleSx}>Quotation Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Quote Number" value={loadingNumber ? 'Loading...' : quoteNumber ?? ''} fullWidth InputProps={{ readOnly: true }} />
              </Grid>
              {[
                ['Quote Date', 'quoteDate'],
                ['Requested Date', 'requestedDate'],
                ['Work Start Date', 'workStartDate'],
                ['Valid Until', 'validUntil']
              ].map(([label, name]) => (
                <Grid item xs={12} sm={6} md={4} key={name}>
                  <TextField
                    label={label}
                    type="date"
                    name={name}
                    value={quoteDetails[name]}
                    onChange={updateQuoteDetail}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              ))}
              {[
                ['Contract / PO No', 'contractNo'],
                ['Project / Reference No', 'projectRefNo'],
                ['Payment Terms', 'paymentTerms']
              ].map(([label, name]) => (
                <Grid item xs={12} sm={6} md={4} key={name}>
                  <TextField
                    label={label}
                    name={name}
                    value={quoteDetails[name]}
                    onChange={updateQuoteDetail}
                    fullWidth
                  />
                </Grid>
              ))}
            </Grid>

            <Typography variant="h6" sx={sectionTitleSx}>Products / Services</Typography>
            {products.map((product, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  mb: 2,
                  p: { xs: 2, sm: 2.5 },
                  borderRadius: 3,
                  background: 'linear-gradient(145deg, #FFFFFF 0%, #F8FAFC 100%)',
                  border: '1px solid #E0E7EF',
                  boxShadow: '0 10px 28px rgba(11,31,58,.06)'
                }}
              >
                <Typography sx={{ mb: 1.5, color: '#0B1F3A', fontWeight: 800 }}>
                  Item {index + 1}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Description of Work / Goods"
                      name="description"
                      value={product.description}
                      onChange={(event) => updateProduct(index, event)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Unit Rate (SAR)"
                      name="unitRate"
                      type="number"
                      value={product.unitRate}
                      onChange={(event) => updateProduct(index, event)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Quantity"
                      name="quantity"
                      type="number"
                      value={product.quantity}
                      onChange={(event) => updateProduct(index, event)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      select
                      label="Unit"
                      name="unit"
                      value={product.unit}
                      onChange={(event) => updateProduct(index, event)}
                      fullWidth
                    >
                      {['UNIT', 'HOURLY', 'DAYS', 'METER'].map((unit) => (
                        <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="VAT Rate (%)"
                      name="vatRate"
                      type="number"
                      value={product.vatRate}
                      onChange={(event) => updateProduct(index, event)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField label="Subtotal (SAR)" value={formatAmount(lineAmounts[index].subtotal)} fullWidth InputProps={{ readOnly: true }} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField label="Total incl. VAT (SAR)" value={formatAmount(lineAmounts[index].total)} fullWidth InputProps={{ readOnly: true }} />
                  </Grid>
                </Grid>
                {products.length > 1 && (
                  <Button
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setProducts((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                    sx={{ mt: 1 }}
                  >
                    Remove Item
                  </Button>
                )}
              </Paper>
            ))}

            <Button
              variant="outlined"
              onClick={() => setProducts((current) => [...current, createProduct()])}
              sx={{ mb: 2, borderColor: '#0B1F3A', color: '#0B1F3A' }}
            >
              + Add Item
            </Button>

            <Paper
              elevation={0}
              sx={{
                mt: 1,
                p: { xs: 2, sm: 3 },
                borderRadius: 3,
                background: 'linear-gradient(135deg, #08192E 0%, #0B1F3A 55%, #173D68 100%)',
                color: '#FFFFFF'
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, color: '#FFFFFF' }}>Quotation Summary</Typography>
              <Grid container spacing={1.5}>
                {[
                  ['Total Excl. VAT', totals.subtotal],
                  ['Total VAT', totals.vat],
                  ['Total Incl. VAT', totals.total]
                ].map(([label, amount]) => (
                  <Grid item xs={12} sm={4} key={label}>
                    <div style={{ padding: 16, minHeight: 82, borderRadius: 12, background: 'rgba(255,255,255,.09)', border: '1px solid rgba(255,255,255,.12)' }}>
                      <Typography variant="body2" sx={{ color: '#B9C6D5', fontWeight: 700 }}>{label}</Typography>
                      <Typography variant="h6" sx={{ mt: 0.5, color: '#FF9A78', fontWeight: 900 }}>
                        {formatAmount(amount)} SAR
                      </Typography>
                    </div>
                  </Grid>
                ))}
              </Grid>
            </Paper>

            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap', marginTop: 24 }}>
              <Button
                variant="contained"
                onClick={printQuote}
                sx={{ minWidth: 150, bgcolor: '#E85D3F', color: '#0B1F3A', '&:hover': { bgcolor: '#C9432B' } }}
              >
                Print Quote
              </Button>
              <Button
                variant="contained"
                onClick={saveQuote}
                disabled={loadingNumber || !Number.isInteger(quoteNumber)}
                sx={{ minWidth: 150, bgcolor: '#0B1F3A', '&:hover': { bgcolor: '#061426' } }}
              >
                Save Quote
              </Button>
            </div>
          </div>
        </Paper>
      </div>
    </ThemeProvider>
  );
}
